import { call, select } from 'redux-saga/effects';
import { eventBus, VirtualBackgroundOptions } from 'utils/eventBus';
import { validateImageURL, validateVideoURL } from 'utils/validateAssetURL';
import {
  selectVbProviderV2Enabled,
  selectVirtualBackgrounds,
} from 'features/virtual-backgrounds/selectors';
import {
  SelectedVirtualBackground,
  VirtualBackgroundItem,
} from 'features/virtual-backgrounds/types';
import { userAgentDetails } from 'utils/userAgentDetails';

// TODO make it more modular and extendable utilizing Zod
export function* validateVBOptionsSaga(options: Partial<VirtualBackgroundOptions>) {
  const backgrounds: VirtualBackgroundItem[] = yield select(selectVirtualBackgrounds);

  if (options.imageUrl) {
    const validUrl: boolean = yield call(validateImageURL, options.imageUrl);

    let validThumbnailUrl: boolean = false;
    if (options.thumbnailUrl) {
      validThumbnailUrl = yield call(validateImageURL, options.thumbnailUrl);
      if (!validThumbnailUrl) {
        yield call(eventBus.error, {
          name: 'virtual-bg',
          message:
            'Provided image thumbnail URL is invalid. The original image URL will be used instead. Please ensure it is publicly accessible and not blocked by CORS or a proxy',
        });
      }
    }

    const config: SelectedVirtualBackground = {
      id: `custom-image-${Date.now()}`, // Generate a unique ID
      type: 'image',
      value: options.imageUrl,
      thumbnail: validThumbnailUrl ? options.thumbnailUrl : options.imageUrl,
      label: 'Custom Image',
    };

    if (validUrl) {
      return { valid: true, config, targetOption: 'imageUrl' };
    }

    if (options.blur || options.image || options.video || options.videoUrl) {
      yield call(eventBus.error, {
        name: 'virtual-bg',
        message: 'Provided image URL is invalid. Will fall back to other options.',
      });
    } else {
      // Legacy wording kept verbatim: integrators may match on this text.
      yield call(eventBus.virtualBackgroundNotApplied, config, 'failed', {
        message:
          'Provided image URL is invalid. Please make sure that it’s publicly available and not blocked by CORS or proxy',
        enforced: !!options.enforce,
      });

      return { valid: false };
    }
  }

  if (options.videoUrl) {
    const validUrl: boolean = yield call(validateVideoURL, options.videoUrl);
    let validThumbnailUrl: boolean = false;

    if (options.thumbnailUrl) {
      validThumbnailUrl = yield call(validateImageURL, options.thumbnailUrl);
    }

    if (validUrl && validThumbnailUrl) {
      return {
        valid: true,
        config: {
          id: `custom-video-${Date.now()}`, // Generate a unique ID
          type: 'video',
          value: options.videoUrl,
          thumbnail: options.thumbnailUrl,
          label: 'Custom Video',
        },
        targetOption: 'videoUrl',
      };
    }

    if (options.blur || options.image || options.video) {
      yield call(eventBus.error, {
        name: 'virtual-bg',
        message: 'Provided video or thumbnail URL is invalid. Will fall back to other options.',
      });
    } else {
      yield call(eventBus.error, {
        name: 'virtual-bg',
        message:
          'Provided video or thumbnail URL is invalid. Please ensure it is publicly accessible and not blocked by CORS or a proxy',
      });

      return { valid: false };
    }
  }

  if (options.image) {
    const imageBackground = backgrounds.find(
      (bg) => bg.id === options.image && bg.type === 'image'
    );

    if (imageBackground) {
      return {
        valid: true,
        config: imageBackground,
        targetOption: 'image',
        thumbnail: imageBackground.thumbnail,
      };
    }

    if (options.blur) {
      yield call(eventBus.error, {
        name: 'virtual-bg',
        message: 'Provided image id is invalid. Will fall back to other options.',
      });
    } else {
      yield call(eventBus.error, {
        name: 'virtual-bg',
        message: 'Provided image id is invalid.',
      });
      return { valid: false };
    }
  }

  if (options.video) {
    const videoBackground = backgrounds.find(
      (bg) => bg.id === options.video && bg.type === 'video'
    );

    if (videoBackground) {
      return {
        valid: true,
        config: videoBackground,
        targetOption: 'video',
        thumbnail: videoBackground.thumbnail,
      };
    }

    if (options.blur) {
      yield call(eventBus.error, {
        name: 'virtual-bg',
        message: 'Provided video id is invalid. Will fall back to other options.',
      });
    } else {
      yield call(eventBus.error, {
        name: 'virtual-bg',
        message: 'Provided video id is invalid.',
      });
      return { valid: false };
    }
  }

  if (options.blur) {
    const { browser }: UAParser.IResult = yield call(userAgentDetails);
    const isSafari = browser.name?.toLowerCase().includes('safari');
    const vbProviderV2Enabled: boolean = yield select(selectVbProviderV2Enabled);

    if (isSafari && !vbProviderV2Enabled) {
      yield call(eventBus.error, {
        name: 'virtual-bg',
        message: 'Blur backgrounds are not supported in Safari.',
      });

      return { valid: false };
    }

    const blurBackground = backgrounds.find(
      (bg) => bg.type === 'blur' && bg.value === options.blur
    );

    if (blurBackground) {
      return {
        valid: true,
        config: blurBackground,
        targetOption: 'blur',
      };
    }

    yield call(eventBus.error, {
      name: 'virtual-bg',
      message: 'Provided blur setting is invalid.',
    });

    return { valid: false };
  }

  yield call(eventBus.error, {
    name: 'virtual-bg',
    message:
      'Missing required options for virtual background. Please provide either "blur", "image" or "imageUrl" property.',
  });

  return { valid: false };
}
